import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '../LoadingSpinner';

/**
 * Protected Route for Partner Pages
 * 
 * Security: Only actual partners (users with a Partner record) can access partner routes.
 * Family members of partner-created families should NOT have access, even if their
 * family has a partnerId.
 * 
 * Checks:
 * 1. User is authenticated
 * 2. Partner account exists (via /api/partner/check-status) and is active
 * 3. OR user has partner_admin role AND Partner record exists and is active
 */
export default function PartnerProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, error } = useAuth0();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkPartnerAccess = async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        setIsAuthorized(false);
        setChecking(false);
        return;
      }

      try {
        // Get access token
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

        // Check 1: Use check-status endpoint first (doesn't require role, auto-activates approved partners)
        const checkStatusResponse = await fetch(`${API_BASE_URL}/api/partner/check-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (checkStatusResponse.ok) {
          const checkData = await checkStatusResponse.json();
          if (checkData.isPartner && checkData.partner?.status === 'active') {
            setIsAuthorized(true);
            setChecking(false);
            return;
          }
        }

        // Check 2: Auth0 partner_admin role (for partner dashboard access)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload['https://eternavaults.com/roles'] || [];
        const hasPartnerAdminRole = roles.includes('partner_admin');

        if (hasPartnerAdminRole) {
          // Verify partner account exists and is active
          const partnerResponse = await fetch(`${API_BASE_URL}/api/partner/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (partnerResponse.ok) {
            const data = await partnerResponse.json();
            if (data.partner && data.partner.status === 'active') {
              setIsAuthorized(true);
              setChecking(false);
              return;
            }
          }
        }

        // No access - only actual partners (with Partner record) can access partner portal
        // Family members of partner-created families should NOT have access
        setIsAuthorized(false);
      } catch (error) {
        console.error('Error checking partner access:', error);
        setIsAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    checkPartnerAccess();
  }, [isAuthenticated, isLoading, user, getAccessTokenSilently]);

  if (isLoading || checking) {
    return <LoadingSpinner type="page" message="Verifying partner access..." />;
  }

  if (!isAuthenticated || !isAuthorized) {
    return <Navigate to="/partner/login" state={{ from: location }} replace />;
  }

  return children;
}

